import { IconFa, PlainButton } from '@glow/ui-components'
import i18next from 'i18next'
import { FormEvent, useState } from 'react'
import { useLocalizeLabels } from './AiAnswer'
import { AiCopyButton } from './AiCopyButton'
import { AiChatMessage } from './useAiChat'

interface Props {
  message: AiChatMessage
  onRate: (messageId: string, helpful: boolean) => void
  onComment: (messageId: string, comment: string) => void
}

const ThumbButton = ({ helpful, selected, onClick }: { helpful: boolean; selected: boolean; onClick: () => void }) => (
  <PlainButton
    onClick={onClick}
    aria-label={i18next.t(helpful ? 'aiChat.helpful' : 'aiChat.notHelpful')}
    aria-pressed={selected}
    className={`rounded px-1.5 py-1 ${selected ? 'bg-primary-050 text-primary-900' : 'text-emphasis-medium hover:text-emphasis-high'}`}
  >
    <IconFa icon={[selected ? 'fas' : 'far', helpful ? 'thumbs-up' : 'thumbs-down']} size="sm" />
  </PlainButton>
)

// Rendered only for persisted answers; a rating posts immediately, the optional comment patches it
export const AiFeedbackRow = ({ message, onRate, onComment }: Props) => {
  const [comment, setComment] = useState('')
  const localize = useLocalizeLabels()

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (comment.trim()) {
      onComment(message.id, comment)
    }
  }

  return (
    <div className="mt-1 flex flex-col gap-2">
      <div className="flex items-center gap-1">
        <ThumbButton helpful selected={message.rating === true} onClick={() => onRate(message.id, true)} />
        <ThumbButton helpful={false} selected={message.rating === false} onClick={() => onRate(message.id, false)} />
        <AiCopyButton text={localize(message.text, message.labelKeys)} />
      </div>
      {message.rating !== undefined && !message.commented && (
        <form onSubmit={onSubmit} className="flex flex-col gap-1">
          <label htmlFor={`ask-glow-comment-${message.id}`} className="text-xs text-emphasis-medium">
            {i18next.t('aiChat.commentLabel')}{' '}
            <span className="text-emphasis-low">({i18next.t('aiChat.commentOptional')})</span>
          </label>
          <div className="flex items-center gap-2">
            <input
              id={`ask-glow-comment-${message.id}`}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder={i18next.t('aiChat.commentPlaceholder')}
              className="h-8 w-full rounded border border-gray-300 px-2 text-sm text-emphasis-high outline-none focus:border-primary-500"
            />
            <PlainButton type="submit" aria-label={i18next.t('aiChat.send')} disabled={!comment.trim()}>
              <IconFa icon={['far', 'paper-plane-top']} size="sm" className="text-primary-500" />
            </PlainButton>
          </div>
        </form>
      )}
      {message.commented && <span className="text-xs text-emphasis-medium">{i18next.t('aiChat.feedbackThanks')}</span>}
    </div>
  )
}
